/* eslint-disable @typescript-eslint/no-shadow */
/*
 * @Author: 张印 hbzyin@163.com
 * @FilePath: /code/mall-fe/src/comps/AreaCascader.tsx
 * @Description: 地区级联选择组件
 *
 */
import { getAreas } from 'api/user';
import IPSwitchModal from 'comps/IPSwitchModal';
import IconArrowUp from 'icons/icon-arrow-up.svg';
import IconClose from 'icons/icon-close.svg';
import { DEFAULT_COOKIE_AREASYSNO } from 'libs/cons';
import { SiteContext } from 'libs/contexts';
import { emptyStyles } from 'libs/utils';
import { forEachRight } from 'lodash';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { cls } from 'reactutils';

interface AreaItem {
  sysNo: string;
  parentSysNo: string;
  hasChild: boolean;
  hasSku?: boolean;
  name: string;
  active: boolean;
  type: number;
}

type QueryParam = {
  sysNo?: string;
  parentSysNo?: string;
  type?: number;
  skuSysNo?: string;
  agreementSysNo?: string;
};

type AreaCascaderProps = {
  onChange?: (value: string[], reloadAreaInfo?) => void; //回调 返回已选区域sysNo集合 ['1150', '1165']
  value?: string | string[]; //默认值  '1165' | ['1150', '1165'](取数组最后一个值) ｜ ''(可以传空)
  areaLevel?: number; // 区域显示层级  默认：2 (市级)  目前区域api最大4级
  curLevelAreaName?: boolean; // 显示当前层级的区域名称
  separator?: string; // 分隔符 默认 /
  styles?: string;
  clearable?: boolean;
  placeholder?: string;
  trigger?: 'hover' | 'click';
  enableIPSwitch?: boolean;
  disabled?: boolean;
  arrowIcon?: React.ReactNode;
  skuSysNo?: string; // 商品sysNo
  agreementSysNo?: string; // 商品交易规则sysNo
};
/**
 * 获取地区省份信息
 * @param {QueryParam} param  查询参数
 * @returns {unknown}
 */
async function getAreaList(param: QueryParam) {
  const resp = await getAreas({
    method: 'POST',
    json: {
      ...param,
    },
  });
  if (resp.code === 200) {
    const { province, city, region, town } = resp.data;
    const data = [province, city, region, town].filter(
      (item) => item instanceof Array && item.length,
    );
    return data;
  } else {
    return [];
  }
}
export const LAST_LOCATION_KEY = 'last_location';

/**
 * 地区展示组件
 * @date 2023/8/25 - 20:01:06
 *
 * @export
 * @param {AreaCascaderProps} {
  onChange,
  value,
  areaLevel = 2,
  curLevelAreaName = false,
  separator = ' / ',
  // @ts-ignore
  styles = emptyStyles,
  clearable = false,
  placeholder,
  trigger = 'hover',
  enableIPSwitch = false,
  disabled = false,
  arrowIcon,
}
 * @returns {*}
 */
export default function AreaCascader({
  onChange,
  value,
  areaLevel = 2,
  curLevelAreaName = false,
  separator = ' / ',
  // @ts-ignore
  styles = emptyStyles,
  clearable = false,
  placeholder,
  trigger = 'hover',
  enableIPSwitch = false,
  disabled = false,
  arrowIcon,
  skuSysNo,
  agreementSysNo,
}: AreaCascaderProps) {
  const site = useContext(SiteContext);
  const [visible, setVisible] = useState(false);
  const [area, setArea] = useState([]);
  const [selectArea, setSelectArea] = useState([]); // 选中地区
  const [activeIndex, setActiveIndex] = useState(0); // 当前选中下标
  const [fullAreaName, setFullAreaName] = useState(''); // 地区全称
  const containerRef = useRef<HTMLDivElement>();
  // 设置显隐之前,统一拦截一下，看一下是否禁用
  const beforeSetVisible = (_visible: boolean) => {
    if (!_visible) {
      setVisible(_visible);
    } else {
      if (!disabled) {
        setVisible(_visible);
      }
    }
  };

  useEffect(() => {
    let isUnmount = false;
    if (value === DEFAULT_COOKIE_AREASYSNO) return;
    let sysNo = '';
    /**
     * 兼容AreasPicker数组传参
     * 判断value是否是数组 是数组取最后一位
     */
    if (value instanceof Array) {
      sysNo = value[value.length - 1];
    } else {
      sysNo = value || '';
    }
    // 组装查询参数
    const param: QueryParam = {};
    if (sysNo) {
      param.sysNo = sysNo;
    } else {
      param.type = 1;
    }

    if (!sysNo && areaLevel === 1) return;
    if (skuSysNo) {
      param.skuSysNo = skuSysNo;
      param.agreementSysNo = agreementSysNo || null;
    }
    // 如果有传入 sysNo 则逆向查询 需处理data
    getAreaList(param).then((data) => {
      if (!isUnmount) {
        //获取地区信息并组装
        const areaList = sysNo ? dataAdapter(data) : data;
        let selectList = filterSelectArea(areaList);
        if (selectList.length <= 0) {
          selectList.push({
            name: '请选择地址',
            sysNo: null,
          });
        } else {
          selectList = selectList.splice(0, areaLevel);
        }
        setActiveIndex(selectList.length - 1);
        setSelectArea(selectList);
        setArea(areaList);
        if (sysNo) {
          let areaName = '';
          if (curLevelAreaName) {
            areaName = selectList[selectList.length - 1].name;
          } else {
            areaName = selectList.map((area: AreaItem) => area.name).join(separator);
          }
          setFullAreaName(areaName);
        } else {
          setFullAreaName('');
        }
      }
    });
    return () => {
      // 清除副作用
      isUnmount = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = useCallback((selectData) => {
    let areaName = '';
    if (curLevelAreaName) {
      areaName = selectData[selectData.length - 1].name;
    } else {
      areaName = selectData.map((area: AreaItem) => area.name).join(separator);
    }
    setFullAreaName(areaName);
    beforeSetVisible(false);
    onChange && onChange(selectData.map((area: AreaItem) => area.sysNo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadArea = useCallback(
    (item, index) => {
      const tempArea = area.concat();
      const filterArea = tempArea.splice(0, index + 1);
      const tempSelectArea = [...selectArea];
      // 重置active状态
      filterArea[filterArea.length - 1].forEach((item: AreaItem) => (item.active = false));
      if (!item.hasChild || index + 1 >= areaLevel) {
        tempSelectArea.pop();
        tempSelectArea.push({
          name: item.name,
          sysNo: item.sysNo,
        });
        item.active = true;
        setSelectArea(tempSelectArea);
        // setEnableMouseLeave(true)
        return handleChange(tempSelectArea);
      }
      // setEnableMouseLeave(false)
      // 正向查询
      getAreaList({ parentSysNo: item.sysNo, type: index + 1, skuSysNo, agreementSysNo }).then(
        (data) => {
          const dataList = filterArea.concat(data);
          const areaList = dataAdapter(dataList);
          const selectList = filterSelectArea(areaList);
          if (selectList.length < areaLevel) {
            selectList.push({
              name: '请选择地址',
              sysNo: null,
            });
          }
          setSelectArea(selectList);
          setArea(areaList);
          setTimeout(() => {
            setActiveIndex(selectList.length - 1);
          }, 200);
        },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [area, selectArea],
  );

  const handleClear = useCallback(
    (event) => {
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
      const tempArea = area.concat();
      tempArea[0].forEach((item: AreaItem) => (item.active = false));
      setArea([tempArea[0]]);
      setActiveIndex(0);
      setFullAreaName('');
      setSelectArea([
        {
          name: '请选择地址',
          sysNo: null,
        },
      ]);
      return onChange && onChange([]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [area],
  );

  // const handleAllArea = useCallback(
  //   (index) => {
  //     const selectList = [...selectArea];
  //     const tempSelectArea = selectList.splice(0, index);
  //     setActiveIndex(index - 1);
  //     setSelectArea(tempSelectArea);
  //     return handleChange(tempSelectArea);
  //   },
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  //   [selectArea],
  // );

  const handleChangeTab = useCallback((index) => {
    setActiveIndex(index);
  }, []);

  const handleHide = useCallback(
    (ev) => {
      if (trigger === 'hover') {
        beforeSetVisible(false);
      } else {
        if (!containerRef.current?.contains(ev.target)) {
          beforeSetVisible(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trigger],
  );

  const handleStopEvent = useCallback((event) => {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleHide);
    return () => {
      document.removeEventListener('click', handleHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listeners =
    trigger === 'hover'
      ? {
          onMouseOver: () => {
            if (!visible) {
              beforeSetVisible(true);
            }
          },
          onMouseLeave: () => {
            beforeSetVisible(false);
          },
        }
      : {
          onClick: () => beforeSetVisible(!visible),
        };

  const handleChangeToIP = (cityInfo) => {
    onChange([cityInfo.sysNo], true);
  };
  return (
    <div className="cascader" {...listeners} ref={containerRef}>
      <div className={cls('cascader-hd', fullAreaName && 'cascader-hd-hover')}>
        <div className="cascader-hd-val" title={fullAreaName || placeholder || '所有区域'}>
          {fullAreaName || placeholder || '所有区域'}
        </div>
        <div className="cascader-hd-icon">
          {arrowIcon || (
            <span className={cls('arrow-icon', visible && 'arrow-up')}>
              <IconArrowUp />
            </span>
          )}
          {clearable && fullAreaName && (
            <span className="clear-icon" onClick={handleClear}>
              <IconClose />
            </span>
          )}
        </div>
      </div>
      {visible && area.length > 0 && (
        <>
          <div className={cls('cascader-bd', visible && 'animate')} onClick={handleStopEvent}>
            <div className="cascader-bd-head">
              {selectArea.map((item, index) => (
                <span
                  className={cls('head-item', index === activeIndex && 'active')}
                  onClick={() => handleChangeTab(index)}
                  key={index}
                >
                  {item.name}
                </span>
              ))}
            </div>
            <div className="cascader-bd-body">
              {area.map((list, index) => (
                <div
                  className={cls('cascader-item', index === activeIndex && 'active')}
                  key={index}
                >
                  {list.length > 0 &&
                    list.map((item: AreaItem) => (
                      <span
                        className={cls(
                          'area-item',
                          item.active && 'active',
                          skuSysNo && !item.hasSku && 'disabled',
                        )}
                        onClick={() => handleLoadArea(item, index)}
                        key={item.sysNo}
                      >
                        {item.name}
                      </span>
                    ))}
                </div>
              ))}
            </div>
          </div>
          <div
            className="cascader-modal"
            onClick={(e) => {
              beforeSetVisible(false);
              handleStopEvent(e);
            }}
          />
        </>
      )}
      {enableIPSwitch && (
        <div onMouseOver={(e) => e.stopPropagation()}>
          <IPSwitchModal fullAreaName={fullAreaName} onChange={handleChangeToIP} value={value} />
        </div>
      )}
      <style jsx>{`
        .cascader {
          width: auto;
          position: relative;
          cursor: default;
        }
        .cascader-hd {
          position: relative;
          cursor: ${disabled ? 'not-allowed' : 'pointer'};
          padding-right: 20px;
        }
        .cascader-hd-hover:hover .clear-icon {
          display: inline-block;
        }
        .cascader-hd-icon {
          position: absolute;
          width: 20px;
          height: auto;
          text-align: center;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
        }
        .arrow-icon {
          display: inline-block;
          vertical-align: middle;
          width: 12px;
          height: 12px;
          line-height: 12px;
          text-align: center;
          transform: rotate(180deg);
          transition: transform 0.3s ease;
        }
        .arrow-icon.arrow-up {
          transform: rotate(0deg);
        }
        .arrow-icon :global(svg) {
          width: 12px;
          height: 12px;
          fill: #999;
        }
        .clear-icon {
          display: none;
          vertical-align: middle;
          width: 12px;
          height: 12px;
          color: #999;
        }
        .clear-icon:hover {
          color: #666;
        }
        .clear-icon :global(svg) {
          font-size: 12px;
          vertical-align: top;
        }
        .cascader-bd {
          position: absolute;
          min-width: 410px;
          width: auto;
          height: auto;
          left: -1px;
          top: 100%;
          background-color: #fff;
          box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.1);
          border: 1px solid #e8e8e8;
          z-index: 3;
        }
        .cascader-modal {
          position: absolute;
          min-width: 410px;
          height: 180px;
          left: -1px;
          top: 100%;
          background-color: transparent;
          z-index: 2;
        }
        .animate {
          animation: slideUpIn 0.5s cubic-bezier(0.23, 1, 0.32, 1);
        }
        .cascader-bd-head {
          height: 30px;
          white-space: nowrap;
          border-bottom: 1px solid #e8e8e8;
          background-color: #f9f9f9;
        }
        .head-item {
          display: inline-block;
          vertical-align: top;
          font-size: 12px;
          line-height: 30px;
          padding: 0 12px;
          border-right: 1px solid #e8e8e8;
          color: #333;
          outline: none;
          cursor: pointer;
        }
        .head-item:hover {
          color: ${site?.global?.platformMainColor};
        }
        .head-item.active {
          height: 30px;
          background-color: #fff;
          text-decoration: none;
        }
        .head-item.active:hover {
          color: #333;
        }
        .cascader-bd-body {
          min-height: 80px;
          padding: 10px;
          padding-bottom: 6px;
        }
        .cascader-item {
          display: none;
          line-height: 1;
        }
        .cascader-item.active {
          display: block;
        }
        .area-item {
          display: inline-block;
          vertical-align: top;
          height: 18px;
          line-height: 18px;
          padding: 0 5px;
          font-size: 12px;
          margin-right: 10px;
          margin-bottom: 4px;
          color: #666;
          cursor: pointer;
        }
        .area-item:hover {
          color: ${site?.global?.platformMainColor};
        }
        .area-item.active {
          background-color: ${site?.global?.platformMainColor};
          color: #fff;
        }
        .area-item.active:hover {
          color: #fff;
        }
        .area-item.disabled {
          color: #ccc;
          cursor: not-allowed;
          pointer-events: none;
        }
        @keyframes slideUpIn {
          0% {
            transform: scaleY(0.75);
            transform-origin: 0% 0%;
            opacity: 0;
          }
          100% {
            transform: scaleY(1);
            transform-origin: 0% 0%;
            opacity: 1;
          }
        }
      `}</style>
      <style jsx>{styles}</style>
    </div>
  );
}

// 逆向找父节点
/**
 * Description placeholder
 * @date 2023/8/25 - 20:01:06
 *
 * @param {*} data
 * @returns {*}
 */
function dataAdapter(data) {
  forEachRight(data, (item, index: number) => {
    item.forEach((items) => {
      const { parentSysNo, active } = items || {};
      if (active) {
        items.active = true;
      }
      data[index - 1] &&
        data[index - 1].forEach((newItem) => {
          const { sysNo } = newItem || {};
          if (sysNo === parentSysNo) {
            newItem.active = true;
          }
        });
    });
  });
  return data;
}

/**
 * Description placeholder
 * @date 2023/8/25 - 20:01:06
 *
 * @param {*} data
 * @returns {*}
 */
function filterSelectArea(data) {
  return data.reduce((acc, item) => {
    const activeItem = item.filter((item) => item.active);
    if (activeItem.length > 0) {
      acc.push({
        name: activeItem[0].name,
        sysNo: activeItem[0].sysNo,
      });
    }
    return acc;
  }, []);
}
